import React, {useEffect, useState} from 'react'
import {Stage} from "react-konva";
import reactImageSize from 'react-image-size';
import {firebaseStorage, storageRef} from "../../../../firebase/client-app";
import {getDownloadURL} from "@firebase/storage";
import {
    EyeIcon,
    EyeSlashIcon,
    ArrowPathIcon,
    MagnifyingGlassPlusIcon,
    MagnifyingGlassMinusIcon
} from "@heroicons/react/24/solid";
import _ from "lodash";
import RenderLayers from "./render-layers";
import {
    DESIGN_IMAGES_PATH,
    DESIGN_KEY_PREFIX,
    QUOTE_TEMPLATE_IMAGES_PATH, STAGE_ZOOM_STEP,
    THUMB_PREFIX
} from "../../../constants/design.constant";
import {uploadSingleImageFile} from "../../../services/design-variant-file-data-service";
import {resizeDataURL} from "../../../../common/utils/image-util";
import {addSingleQuoteTemplate} from "../../../services/quote-template-data-service";
import shortid from "shortid";
import PreviewQuoteTemplates from "../../quote-template/preview-quote-templates";
import {getNextItemKey} from "../../../../common/utils/item-key-util";
import Konva from "konva";

function downloadURI(uri, name) {
    let link = document.createElement('a');
    link.download = name;
    link.href = uri;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const DesignVariantKonvaEditor = ({
                                      settings,
                                      currentImage,
                                      newImage,
                                      designVariantDetail,
                                      setDesignVariantDetail,
                                      designDetail,
                                      productColor,
                                      duplicateDesignVariant,
                                      updateVariantColorsInDesign,
                                      uploadImage,
                                      setUploadImage,
                                      setImageUploadInProgress,
                                      changesCount,
                                      applySelectedStyle,
                                  }) => {
    const [initialStep, setInitialStep] = useState(true);
    const stageRef = React.useRef(null);
    const backgroundRef = React.useRef(null);
    const transformerRef = React.useRef(null);
    const selectionRectangleRef = React.useRef(null);
    const gridLayerRef = React.useRef(null);
    const [stagePos, setStagePos] = useState({x: settings?.x || 0, y: settings?.y || 0});
    const [stageScale, setStageScale] = useState(settings.scale || 1);
    const [stageScalePercentage, setStageScalePercentage] = useState(100);
    const [stageWidth, setStageWidth] = useState(settings?.width);
    const [stageHeight, setStageHeight] = useState(settings?.height);
    const [images, setImages] = React.useState([]);
    const [selectedText, setSelectedText] = useState(null);
    const [selectedImage, setSelectedImage] = React.useState(null);
    const [uploadTemplateInProgress, setUploadTemplateInProgress] = React.useState(false);
    const [colorExistInDesignVariant, setColorExistInDesignVariant] = React.useState(false);
    const [selectedEdges, setSelectedEdges] = useState({x1: 0, y1: 0, x2: 0, y2: 0});
    const [showHighlightLines, setShowHighlightLines] = React.useState(true);
    const [showStage, setShowStage] = useState(false);

    useEffect(() => {
        if (changesCount === 0) {
            // stageRef.current.draw();
            refreshStage();
        }
    }, [changesCount])

    useEffect(() => {
        changeStageScale(stageScale);
    }, [settings?.width, settings?.height])

    useEffect(() => {
        if (!_.isEmpty(newImage)) {
            if (newImage?.isAdded) {
                processAddedImageLayer(newImage);
                newImage.isAdded = false;
            }
        }
    }, [newImage])

    const handleImageDragOver = async (event) => {
        event.preventDefault();
    }

    const setCurrentImageSize = () => {
        return new Promise<void>((resolve) => {
            if (currentImage && (!currentImage.width || !currentImage.height)) {
                reactImageSize(currentImage.downloadUrl)
                    .then(({width, height}) => {
                        currentImage.width = width;
                        currentImage.height = height;

                        resolve();
                    })
                    .catch((errorMessage) => {
                        console.log('errorMessage: ', errorMessage);
                    });
            } else {
                resolve();
            }
        });
    }

    const handleImageDropEnd = async (event) => {
        if (_.isEmpty(event)) {
            event.preventDefault();
        }

        stageRef.current.setPointersPositions(event);

        processAddedImageLayer(currentImage);
    }

    const processAddedImageLayer = async (newOrCurrentImage) => {
        await getImageDownloadUrl();

        if (!newOrCurrentImage.height || !newOrCurrentImage.width) {
            await setCurrentImageSize();
        }

        let height = newOrCurrentImage.height / 4;
        let width = newOrCurrentImage.width / 4;

        let ratio = 1;
        if (newOrCurrentImage.width > newOrCurrentImage.height) {
            ratio = newOrCurrentImage.width / newOrCurrentImage.height;
        } else {
            ratio = newOrCurrentImage.height / newOrCurrentImage.width;
        }

        height = height * ratio;
        width = width * ratio;

        const existingDesignVariantDetail = {...designVariantDetail};
        const existingImages = existingDesignVariantDetail.images || [];

        const stage = stageRef.current;
        const centerX = stage.width() / 2;
        const centerY = stage.height() / 2;

        const imageDetail = {
            key: getNextItemKey(existingImages, DESIGN_KEY_PREFIX.IMAGE),
            x: centerX,
            y: centerY,
            width: width,
            height: height,
            imageWidth: newOrCurrentImage.width,
            imageHeight: newOrCurrentImage.height,
            id: newOrCurrentImage?.id,
            src: newOrCurrentImage?.downloadUrl,
            fileUrl: newOrCurrentImage?.fileUrl,
            displayLabel: newOrCurrentImage?.displayLabel,
            thumbnail: newOrCurrentImage?.thumbnail,
        }

        existingImages.push(imageDetail);
        existingDesignVariantDetail.images = existingImages;

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const checkDeselect = (e) => {
        // deselect when clicked on empty area
        const clickedOnEmpty = e.target === e.target.getStage();
        if (clickedOnEmpty) {
            setSelectedText(null);
            setSelectedImage(null);
        }

        handleMouseDown(e);
    };

    const handleImageAttributeChanges = (imageItem, index, newAttrs) => {
        const existingImages = [...images];
        existingImages[index] = newAttrs;
        setImages(existingImages);
    }

    const getImageDownloadUrl = async () => {
        return new Promise<void>(async (resolve) => {
            const fileRef = storageRef(firebaseStorage, currentImage.name);
            currentImage.downloadUrl = await getDownloadURL(fileRef);
            resolve();
        });
    }

    const fitStageIntoParentContainer = () => {
        const stage = stageRef.current;
        let container = document.querySelector('#stage-parent');
        // @ts-ignore
        let containerWidth = container.offsetWidth * 0.65;
        let scale = containerWidth / settings?.width;

        setStageScale(scale);

        stage.width(settings?.width * scale);
        stage.height(settings?.height * scale);
        stage.scale({x: scale, y: scale});

        /*const stage = stageRef.current;
        const existingScale = settings.scale;

        if (existingScale) {
            stage.width(settings.width * existingScale);
            stage.height(settings.height * existingScale);
            stage.scale({x: existingScale, y: existingScale});
            setStageScale(existingScale);
        } else {
            let container = document.querySelector('#stage-parent');

            // @ts-ignore
            let containerWidth = container.offsetWidth * 0.6;
            let scale = containerWidth / settings.width;
            stage.width(settings.width * scale);
            stage.height(settings.height * scale);
            stage.scale({x: scale, y: scale});

            setStageScale(scale);
        }

        setStageWidth(stage.width());
        setStageHeight(stage.height());

        refreshStage();*/
    }

    const prepareInitialLayers = () => {
        const tempImagesLayer: any = {};
        const tempTextsLayer: any = {};

        tempImagesLayer.images = settings.images;
        tempTextsLayer.textItems = settings.textItems;

        const existingDesignVariantDetail = {...designVariantDetail};
        existingDesignVariantDetail.imagesLayer = tempImagesLayer;
        existingDesignVariantDetail.textsLayer = tempTextsLayer;

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    useEffect(() => {
        if (initialStep) {
            fitStageIntoParentContainer();
            setShowStage(true);
            refreshStage();
        }
    }, [initialStep])

    useEffect(() => {
        let currentStageScale = _.parseInt((stageScale * 100).toString());
        setStageScalePercentage(currentStageScale);
    }, [stageScale])

    const processStageScaleInSettings = (currentStageScale) => {
        const existingDesignVariantDetail = {...designVariantDetail};
        existingDesignVariantDetail.settings.scale = currentStageScale;
        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const toggleShowHighlightLines = (e) => {
        setShowHighlightLines(!showHighlightLines);
    }

    const zoomIn = () => {
        let currentStageScale = stageScale;
        currentStageScale += STAGE_ZOOM_STEP;

        setStageScale(currentStageScale);
        changeStageScale(currentStageScale);
        processStageScaleInSettings(currentStageScale);
    }

    const zoomOut = () => {
        let currentStageScale = stageScale;
        currentStageScale -= STAGE_ZOOM_STEP;

        setStageScale(currentStageScale);
        changeStageScale(currentStageScale);
        processStageScaleInSettings(currentStageScale);
    }

    const changeStageScale = (scale) => {
        const stage = stageRef.current;

        stage.width(settings.width * scale);
        stage.height(settings.height * scale);
        stage.scale({x: scale, y: scale});
        stage.draw();
    }

    const handleExport = () => {
        gridLayerRef.current.visible(false);
        showTemplateImages(false);

        // const pixelRatio = 100 / (stageScale * 100) + 1;
        const pixelRatio = 100 / (stageScale * 100);
        const uri = stageRef.current.toDataURL({
            pixelRatio: pixelRatio
        });

        gridLayerRef.current.visible(true);
        showTemplateImages(true);

        downloadURI(uri, designVariantDetail.imageFileName);
    };

    const showTemplateImages = (showOrHide) => {
        const stage = stageRef.current;
        const templateImages = stage.find('.template-image');
        _.forEach(templateImages, templateImage => {
            templateImage.visible(showOrHide);
        });
    }

    const handleReadyToPublish = async () => {
        /*gridLayerRef.current.visible(false);
        const pixelRatio = 100 / (stageScale * 100) + 1;
        const base64Data = stageRef.current.toDataURL({
            pixelRatio: pixelRatio
        });
        gridLayerRef.current.visible(true);

        const filePath = [];
        filePath.push(_.kebabCase(DESIGN_IMAGES_PATH));
        filePath.push(_.kebabCase(designDetail.podName));
        filePath.push(_.kebabCase(designDetail.market));
        filePath.push(_.kebabCase(designDetail.productType));
        filePath.push(_.kebabCase(designDetail.parent || ''));
        filePath.push(_.kebabCase(designDetail.themeName || ''));
        filePath.push(_.kebabCase(designDetail.quoteName || ''));
        filePath.push(designVariantDetail.imageFileName + '.png');
        const fullFilePath = _.join(_.compact(filePath), '/');

        const metaData = {
            contentType: "image/png",
            customMetadata: {
                designVariantId: designVariantDetail.id,
                designId: designDetail.id,
                isDesignFile: true
            }
        };

        const result = await uploadSingleImageFile(fullFilePath, base64Data, metaData);
        const imageDetail: any = result.metadata;

        _.forIn(imageDetail, (value, field) => {
            if (_.isEmpty(value)) {
                imageDetail[field] = '';
            }
        });*/

        const existingDesignVariantDetail = {...designVariantDetail};
        // existingDesignVariantDetail.imageDetail = imageDetail;
        existingDesignVariantDetail.status = 'ready-to-publish';

        setDesignVariantDetail(existingDesignVariantDetail);
    };

    useEffect(() => {
        if (uploadImage && designVariantDetail.status === 'in-design') {
            handleReadyToPublish();
        }
    }, [uploadImage]);

    const saveAsQuoteTemplate = async () => {
        setUploadTemplateInProgress(true);
        const stage = stageRef.current;
        const pixelRatio = 100 / (stageScale * 100);
        gridLayerRef.current.visible(false);
        const url = stage.toDataURL({pixelRatio: pixelRatio});
        gridLayerRef.current.visible(true);
        const stageRatio = stageWidth / stageHeight;

        let thumbnailHeight = 600;
        let thumbnailWidth = 600;

        if (stageRatio > 1) {
            thumbnailHeight *= stageRatio;
        } else {
            thumbnailWidth *= stageRatio;
        }

        let thumbnailDataUrl = await resizeDataURL(url, thumbnailWidth, thumbnailHeight);

        const quoteTemplateName = _.kebabCase(designDetail.quoteName) + '-' + shortid.generate();
        const filePath = [];
        filePath.push(_.kebabCase(QUOTE_TEMPLATE_IMAGES_PATH));
        filePath.push(_.kebabCase(designDetail.quoteName || ''));
        filePath.push(THUMB_PREFIX + quoteTemplateName + '.png');
        const fullFilePath = _.join(_.compact(filePath), '/');

        const metaData = {
            contentType: "image/png",
            customMetadata: {
                quoteId: designDetail.quoteId,
            }
        };

        const result = await uploadSingleImageFile(fullFilePath, thumbnailDataUrl, metaData);
        const imageDetail: any = result.metadata;

        _.forIn(imageDetail, (value, field) => {
            if (_.isEmpty(value)) {
                imageDetail[field] = '';
            }
        });

        const quoteTemplate: any = {};
        quoteTemplate.name = quoteTemplateName;
        quoteTemplate.quoteId = designDetail.quoteId;
        quoteTemplate.textItems = designVariantDetail.textItems;
        quoteTemplate.settings = designVariantDetail.settings;
        quoteTemplate.shapes = designVariantDetail.shapes || [];
        quoteTemplate.imageDetail = imageDetail;

        await addSingleQuoteTemplate(quoteTemplate);
        setUploadTemplateInProgress(false);
    }

    const handleStageAttributeChanges = (event) => {
        const existingDesignVariantDetail = {...designVariantDetail};
        const existingSettings = existingDesignVariantDetail.settings;
        existingSettings.x = event.currentTarget.attrs.x;
        existingSettings.y = event.currentTarget.attrs.y;

        setDesignVariantDetail(existingDesignVariantDetail);
    }

    const handleMouseDown = (e) => {
        const stage = stageRef.current;

        if (e.target !== stage) {
            return;
        }

        e.evt.preventDefault();
        const currentScale = stage.scaleX();

        const existingSelectedEdges = {...selectedEdges};
        existingSelectedEdges.x1 = stage.getPointerPosition().x / currentScale;
        existingSelectedEdges.y1 = stage.getPointerPosition().y / currentScale;
        existingSelectedEdges.x2 = stage.getPointerPosition().x / currentScale;
        existingSelectedEdges.y2 = stage.getPointerPosition().y / currentScale;

        // console.log('stage.scale: --->', stage.scale())

        setSelectedEdges(existingSelectedEdges)

        selectionRectangleRef.current.visible(true);
        selectionRectangleRef.current.width(0);
        selectionRectangleRef.current.height(0);
    }

    const handleMouseMove = (e) => {
        if (selectionRectangleRef && selectionRectangleRef.current && !selectionRectangleRef.current.visible()) {
            return;
        }

        e.evt.preventDefault();

        const existingSelectedEdges = {...selectedEdges};
        const stage = stageRef.current;
        const currentScale = stage.scaleX();
        existingSelectedEdges.x2 = stage.getPointerPosition().x / currentScale;
        existingSelectedEdges.y2 = stage.getPointerPosition().y / currentScale;

        setSelectedEdges(existingSelectedEdges);

        // console.log(stage.getPointerPosition(), existingSelectedEdges);

        selectionRectangleRef.current.setAttrs({
            x: Math.min(existingSelectedEdges.x1, existingSelectedEdges.x2),
            y: Math.min(existingSelectedEdges.y1, existingSelectedEdges.y2),
            width: Math.abs(existingSelectedEdges.x2 - existingSelectedEdges.x1),
            height: Math.abs(existingSelectedEdges.y2 - existingSelectedEdges.y1),
        });
    }

    const handleMouseUp = (e) => {
        const stage = stageRef.current;

        if (!selectionRectangleRef.current.visible()) {
            return;
        }

        e.evt.preventDefault();
        // update visibility in timeout, so we can check it in click event
        setTimeout(() => {
            selectionRectangleRef.current.visible(false);
        });

        const selectionRectangleBox = selectionRectangleRef.current.getClientRect();
        const allShapes = stage.find('.object');
        const selectedNodes = allShapes.filter((shape) =>
            Konva.Util.haveIntersection(selectionRectangleBox, shape.getClientRect())
        );

        transformerRef.current.nodes(selectedNodes);
        transformerRef.current.getLayer().batchDraw();
    }

    const handleClick = (e, itemDetail) => {
        const stage = stageRef.current;
        // if click on empty area - remove all selections
        if (e.target === stage) {
            transformerRef.current.nodes([]);
            return;
        }

        if (selectionRectangleRef.current.visible()) {
            return;
        }

        // do we pressed shift or ctrl?
        const metaPressed = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
        // const isSelected = transformerRef.current.nodes().indexOf(e.target) >= 0;
        const isSelected = _.findIndex(transformerRef.current.nodes(), ['key', itemDetail?.key]) >= 0;

        if (!metaPressed && !isSelected) {
            // if no key pressed and the node is not selected
            // select just one
            transformerRef.current.nodes([e.target]);
        } else if (metaPressed && isSelected) {
            // if we pressed keys and node was selected
            // we need to remove it from selection:
            const nodes = transformerRef.current.nodes().slice(); // use slice to have new copy of array
            // remove node from array
            nodes.splice(nodes.indexOf(e.target), 1);
            transformerRef.current.nodes(nodes);
        } else if (metaPressed && !isSelected) {
            // add the node into selection
            const nodes = transformerRef.current.nodes().concat([e.target]);
            transformerRef.current.nodes(nodes);
        }

        transformerRef.current.getLayer().batchDraw();
    }

    const refreshStage = () => {
        setTimeout(() => {
            const stage = stageRef.current;
            stage.draw();
        }, 10);
    }

    return (
        <>

            <div className="sticky top-0 left-0 bg-gray-400 z-40 flex justify-between items-center">
                <div className="flex justify-between items-center p-4">
                    <button onClick={zoomOut}
                            className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500">
                        <div className="p-2">
                            <MagnifyingGlassMinusIcon className="h-6 w-6"/>
                        </div>
                    </button>

                    <button
                        className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500 py-2 px-3 ml-2">
                        <div className="font-bold text-gray-600">{stageScalePercentage} %</div>
                    </button>

                    <button onClick={zoomIn}
                            className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500 ml-2">
                        <div className="p-2">
                            <MagnifyingGlassPlusIcon className="h-6 w-6"/>
                        </div>
                    </button>

                    <button onClick={refreshStage}
                            className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500 ml-2">
                        <div className="p-2">
                            <ArrowPathIcon className="h-6 w-6"/>
                        </div>
                    </button>

                    <button onClick={toggleShowHighlightLines}
                            className="rounded flex items-center justify-center border bg-gray-100 text-gray-500 border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500 ml-2">
                        {showHighlightLines &&
                            <div className="p-2">
                                <EyeIcon className="h-6 w-6"/>
                            </div>
                        }

                        {!showHighlightLines &&
                            <div className="p-2">
                                <EyeSlashIcon className="h-6 w-6"/>
                            </div>
                        }
                    </button>
                </div>

                <div className="">
                    {productColor &&
                        <div className="flex justify-start items-center">
                            <div style={{'backgroundColor': `${productColor?.code}`}}
                                 className={`m-1 border border-gray-500 px-3 py-2 rounded flex justify-center 
                                ${productColor?.code ? 'text-white' : 'bg-gray-200 text-black'} ${productColor?.name === 'White' ? 'bg-white text-black' : ''}`}>
                                <div className="self-center">{productColor?.name}</div>
                            </div>
                        </div>
                    }
                </div>

                <div className="flex justify-center mr-4">
                    <div className="ml-2">
                        <PreviewQuoteTemplates
                            quoteId={designDetail?.quoteId}
                            applySelectedStyle={applySelectedStyle}/>
                    </div>

                    {uploadTemplateInProgress &&
                        <button
                            className="px-4 py-2 rounded bg-green-200 border border-green-500 cursor-wait">
                            <div>Saving Template...</div>
                        </button>
                    }

                    {!uploadTemplateInProgress &&
                        <button onClick={saveAsQuoteTemplate}
                                className="px-4 py-2 rounded bg-purple-200 border border-purple-500 hover:text-purple-700 hover:bg-white hover:border-purple-500">
                            <div>Save As Template</div>
                        </button>
                    }

                    <button onClick={handleExport}
                            className="px-4 py-2 rounded bg-gray-100 border border-gray-500 hover:text-gray-700 hover:bg-white hover:border-gray-500 ml-2">Export
                    </button>
                </div>

            </div>

            <div className="design-editor-container overflow-scroll">
                <div id="stage-parent"
                     className="flex justify-start px-4 py-4"
                     onDrop={handleImageDropEnd}
                     onDragOver={handleImageDragOver}>

                    <Stage width={designVariantDetail?.settings?.width} height={designVariantDetail?.settings?.height}
                           style={{'backgroundColor': productColor ? productColor?.code : '#FFFFFF'}}
                           className="flex justify-start border border-gray-800 shadow-lg"
                           onClick={(e) => {
                               handleClick(e, null)
                           }}
                           draggable={false}
                           onMouseDown={handleMouseDown}
                           onTouchStart={handleMouseDown}
                           onMouseMove={handleMouseMove}
                           onMouseUp={handleMouseUp}
                           perfectDrawEnabled={false}
                           ref={stageRef}
                           scaleX={stageScale}
                           scaleY={stageScale}
                           x={stagePos.x}
                           y={stagePos.y}
                           onDragEnd={e => {
                               handleStageAttributeChanges(e);
                               setStagePos(e.currentTarget.absolutePosition());
                           }}
                           onDragMove={e => {
                               backgroundRef.current.absolutePosition({x: 0, y: 0});
                               gridLayerRef.current.absolutePosition({x: 0, y: 0});
                           }}>

                        {showStage &&
                            <RenderLayers
                                designVariantDetail={designVariantDetail}
                                selectedImage={selectedImage}
                                setSelectedImage={setSelectedImage}
                                selectedText={selectedText}
                                setSelectedText={setSelectedText}
                                setDesignVariantDetail={setDesignVariantDetail}
                                backgroundRef={backgroundRef}
                                transformerRef={transformerRef}
                                selectionRectangleRef={selectionRectangleRef}
                                gridLayerRef={gridLayerRef}
                                stageRef={stageRef}
                                stagePos={stagePos}
                                /*stageWidth={stageWidth}
                                stageHeight={stageHeight}*/
                                showHighlightLines={showHighlightLines}
                                handleClick={handleClick}/>
                        }

                    </Stage>
                </div>
            </div>

        </>
    )
}

export default DesignVariantKonvaEditor
